// packages/web-ui/src/components/inline-cards/CardShell.tsx
//
// Shared chrome wrapper for all inline cards (UX §3 design tokens, AC #1, AC #9).
// Un-exported from the public barrel — only sibling card components consume it.
//
// Tokens used (from `styles/tokens.css` Tailwind v4 @theme):
//   - bg-cream-50       → --color-cream-50  (#FFFFFF, root canvas / card body)
//   - border-taupe-300  → --color-taupe-300 (#DDDDDD, hairline border)
//
// UX §6.1.5 inline mode constraint: max-width 640 px. The slot wrapper in
// MessageBubble (W2-S6-integration) ALSO clamps to 640 px; this is belt-and-
// braces.

import type { JSX, ReactNode } from 'react';

export interface CardShellProps {
  /** ARIA role — most cards use 'region'; FileUploadConfirm uses 'alertdialog'. */
  role: 'region' | 'alertdialog';
  /** ARIA label for the card root. */
  ariaLabel: string;
  /** Optional aria-describedby hook (used by FileUploadConfirm). */
  ariaDescribedBy?: string;
  /** Identifies the card kind in E2E selectors (W2-S10 streaming-chat spec). */
  cardKind: string;
  /** Optional `data-testid` for unit-test selectors. */
  testId?: string;
  /** Children — the card's body. */
  children: ReactNode;
  /** Extra Tailwind classes appended to the root. */
  className?: string;
}

/**
 * The shared chrome wrapper. Audi pass — sharp corners, hairline
 * border, white surface, no shadow.
 *
 *   - rounded-none        (0 px — Audi sharp corner geometry)
 *   - border + taupe-300  (1 px hairline border)
 *   - bg-cream-50         (white card surface)
 *   - p-3                 (12 px padding)
 *   - max-w-[640px]       (UX §6.1.5 inline mode)
 *   - text-fg             (near-black body text)
 *
 * The `data-card-kind` attribute is the E2E selector hook for
 * `web-e2e/streaming-chat.spec.ts` (W2-S10) — DO NOT remove.
 */
export function CardShell(props: CardShellProps): JSX.Element {
  const base =
    'rounded-none border border-taupe-300 bg-cream-50 p-3 max-w-[640px] text-fg';
  return (
    <div
      role={props.role}
      aria-label={props.ariaLabel}
      aria-describedby={props.ariaDescribedBy}
      data-card-kind={props.cardKind}
      data-testid={props.testId}
      className={
        props.className === undefined ? base : `${base} ${props.className}`
      }
    >
      {props.children}
    </div>
  );
}
